import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppShell,
  Avatar,
  NavLink,
  Text,
  Group,
  Box,
  UnstyledButton,
  Image,
  Divider,
  Menu,
  Modal,
  Stack,
} from "@mantine/core";
import { BACKENDS, getBackendMode, setBackendMode } from "../../utils/backendConfig";
import {
  IconBuildingStore,
  IconLogout,
  IconX,
  IconTemplate,
  IconDashboard,
  IconMessageCircle,
  IconMail,
  IconUsersGroup,
  IconTool,
  IconChartBar,
  IconGauge,
  IconChevronUp,
  IconSettings,
  IconFileCertificate,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from "@tabler/icons-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import classes from "./AdminSidebar.module.css";

interface NavItem {
  label: string;
  icon: typeof IconDashboard;
  href: string;
}

const CONVERSATION_VIEWS = [
  { label: "List", view: "list" },
  { label: "Graph", view: "graph" },
  { label: "Calendar", view: "calendar" },
] as const;

// Per Charger is its own top-level section (sibling to Conversations).
const CHARGER_VIEWS = [
  { label: "List", view: "list" },
  { label: "Graph", view: "graph" },
  { label: "Calendar", view: "calendar" },
] as const;

// "Users" is intentionally not here — the Team page is the canonical
// user list now (clicking a row opens the same UserDetailContent in a
// drawer). The /admin/users routes still work for direct URL access.
const TOOLS_ITEMS: NavItem[] = [
  { label: "Shops", icon: IconBuildingStore, href: "/admin/shops" },
  { label: "Legal Docs", icon: IconFileCertificate, href: "/admin/legal-documents" },
  { label: "Templates", icon: IconTemplate, href: "/admin/templates" },
  { label: "Email Templates", icon: IconMail, href: "/admin/email-templates" },
];

const navLinkStyles = {
  root: {
    borderRadius: "var(--mantine-radius-md)",
    color: "rgba(0, 0, 0, 0.65)",
    "--nl-bg": "rgba(67, 56, 202, 0.08)",
    "--nl-hover": "rgba(67, 56, 202, 0.06)",
    "--nl-color": "#111827",
  },
  label: { fontSize: "var(--mantine-font-size-sm)" },
};

interface AdminSidebarProps {
  onClose?: () => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

export function AdminSidebar({ onClose, collapsed, onToggleCollapsed }: AdminSidebarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAdminAuth();
  const [backendModalOpen, setBackendModalOpen] = useState(false);
  const [currentBackend, setCurrentBackend] = useState(getBackendMode);
  const logoTapCount = useRef(0);
  const toolsActive = TOOLS_ITEMS.some((t) => pathname.startsWith(t.href));
  const [toolsOpen, setToolsOpen] = useState(toolsActive);
  useEffect(() => {
    // Collapse Tools when navigating away from any of its routes; open it when entering one.
    setToolsOpen(toolsActive);
  }, [toolsActive]);
  const logoTapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLogoClick = () => {
    logoTapCount.current += 1;
    if (logoTapTimer.current) clearTimeout(logoTapTimer.current);
    logoTapTimer.current = setTimeout(() => { logoTapCount.current = 0; }, 2000);
    if (logoTapCount.current >= 20) {
      logoTapCount.current = 0;
      setCurrentBackend(getBackendMode());
      setBackendModalOpen(true);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const colStyle = { display: "flex", flexDirection: "column" as const, height: "100%" };

  // Shared dropdown for the user menu — used by the expanded avatar button and
  // the collapsed rail's gear button.
  const renderUserMenu = () => (
    <Menu.Dropdown>
      {user && (
        <>
          <Menu.Label>
            <Text size="11px" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: "0.04em" }}>
              Signed in
            </Text>
          </Menu.Label>
          <Box px={12} pb={6}>
            <Text size="sm" fw={600} truncate>{user.name}</Text>
            <Text size="11px" c="dimmed" truncate>{user.email}</Text>
            <Group gap={4} mt={4}>
              {user.roles.slice(0, 3).map((r) => (
                <Text key={r} size="10px" c="dimmed" style={{
                  padding: "1px 6px",
                  borderRadius: 4,
                  background: "rgba(0,0,0,0.05)",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}>{r}</Text>
              ))}
            </Group>
          </Box>
          <Menu.Divider />
        </>
      )}
      <Menu.Item leftSection={<IconSettings size={14} stroke={1.75} />} disabled>
        Settings
      </Menu.Item>
      <Menu.Item
        leftSection={<IconLogout size={14} stroke={1.75} />}
        color="red"
        onClick={handleLogout}
      >
        Sign out
      </Menu.Item>
    </Menu.Dropdown>
  );

  // Collapsed desktop rail: reopen button on top, settings gear on the bottom.
  const railContent = (
    <>
      <AppShell.Section p="xs" pt="md">
        <Stack align="center" gap="sm">
          <Box style={{ cursor: "pointer" }} onClick={handleLogoClick}>
            <Image src="/images/haist/haist-logo-square.png" w={28} h={28} fit="contain" style={{ userSelect: "none" }} />
          </Box>
          <UnstyledButton
            onClick={onToggleCollapsed}
            title="Expand sidebar"
            style={{ color: "rgba(0,0,0,0.55)", lineHeight: 1 }}
          >
            <IconLayoutSidebarLeftExpand size={20} />
          </UnstyledButton>
        </Stack>
      </AppShell.Section>
      <AppShell.Section grow />
      <AppShell.Section p="xs" pb="sm">
        <Group justify="center">
          <Menu position="right-end" shadow="md" width={220} withinPortal>
            <Menu.Target>
              <UnstyledButton title="Settings" style={{ color: "rgba(0,0,0,0.55)", lineHeight: 1 }}>
                <IconSettings size={20} stroke={1.75} />
              </UnstyledButton>
            </Menu.Target>
            {renderUserMenu()}
          </Menu>
        </Group>
      </AppShell.Section>
    </>
  );

  const modal = (
    <Modal
      opened={backendModalOpen}
      onClose={() => setBackendModalOpen(false)}
      title="Backend Server"
      size="sm"
      styles={{ title: { fontWeight: 600 } }}
    >
      <Stack gap="xs">
        {(Object.entries(BACKENDS) as [keyof typeof BACKENDS, string][]).map(([mode, url]) => {
          const isActive = currentBackend === mode;
          const modeColor = mode === 'local' ? '#7C3AED' : mode === 'dev' ? '#D97706' : '#2563EB';
          return (
            <UnstyledButton
              key={mode}
              onClick={() => { setBackendModalOpen(false); if (mode !== currentBackend) setBackendMode(mode); }}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: `2px solid ${isActive ? modeColor : 'var(--mantine-color-gray-3)'}`,
                background: isActive ? `${modeColor}14` : 'transparent',
                transition: 'border-color 0.15s',
              }}
            >
              <Group gap="xs" mb={2}>
                <Text size="xs" fw={700} tt="uppercase" style={{ color: isActive ? modeColor : 'var(--mantine-color-gray-7)' }}>{mode}</Text>
                {isActive && <Text size="10px" fw={600} style={{ color: modeColor }}>● active</Text>}
              </Group>
              <Text size="11px" c="dimmed" style={{ wordBreak: 'break-all' }}>{url}</Text>
            </UnstyledButton>
          );
        })}
      </Stack>
    </Modal>
  );

  const fullContent = (
    <>
      <AppShell.Section p="md" pb="xs">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="xs" wrap="nowrap" style={{ position: 'relative' }}>
            <Box style={{ position: 'relative', cursor: 'pointer' }} onClick={handleLogoClick}>
              <Image
                src="/images/haist/haist-logo-square.png"
                w={30}
                h={30}
                fit="contain"
                style={{ flexShrink: 0, userSelect: 'none' }}
              />
            </Box>
            <Box>
              <Text fw={400} size="sm" c="#111827" lh={1.2} style={{ letterSpacing: "0.08em" }}>HAIST</Text>
              <Text size="10px" c="rgba(0,0,0,0.45)" tt="uppercase" style={{ letterSpacing: "0.1em" }}>Admin</Text>
            </Box>
            {currentBackend !== 'prod' && (
              <Box
                onClick={() => { setCurrentBackend(getBackendMode()); setBackendModalOpen(true); }}
                style={{
                  padding: '2px 7px',
                  borderRadius: 20,
                  background: currentBackend === 'local' ? '#7C3AED' : '#D97706',
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                {currentBackend.toUpperCase()}
              </Box>
            )}
            {/* Collapse toggle — right of the logo (desktop only). */}
            <UnstyledButton
              onClick={onToggleCollapsed}
              visibleFrom="sm"
              title="Collapse sidebar"
              style={{
                marginLeft: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: 6,
                border: '1px solid var(--mantine-color-gray-2)',
                color: 'rgba(0,0,0,0.5)',
                lineHeight: 1,
              }}
            >
              <IconLayoutSidebarLeftCollapse size={18} />
            </UnstyledButton>
          </Group>
          <Group gap={4} wrap="nowrap">
            <UnstyledButton
              onClick={onClose}
              hiddenFrom="sm"
              style={{ color: "rgba(0,0,0,0.4)", lineHeight: 1 }}
            >
              <IconX size={16} />
            </UnstyledButton>
          </Group>
        </Group>
      </AppShell.Section>

      <Divider color="var(--mantine-color-gray-2)" />

      <AppShell.Section grow p="xs" pt="sm">
        <NavLink
          label="Dashboard"
          leftSection={<IconDashboard size={18} stroke={1.5} />}
          active={pathname.startsWith("/admin/dashboard")}
          onClick={() => { navigate("/admin/dashboard"); onClose?.(); }}
          mb={2}
          styles={navLinkStyles}
          classNames={{ root: classes.navLink }}
        />

        <NavLink
          label="Conversations"
          leftSection={<IconMessageCircle size={18} stroke={1.5} />}
          childrenOffset={28}
          mt={2}
          defaultOpened={pathname.startsWith("/admin/conversations")}
          active={pathname.startsWith("/admin/conversations")}
          styles={navLinkStyles}
          classNames={{ root: classes.navLink }}
        >
          {CONVERSATION_VIEWS.map((v) => {
            const params = new URLSearchParams(window.location.search);
            const currentView = params.get("view") || "list";
            const isActive = pathname.startsWith("/admin/conversations") && currentView === v.view;
            return (
              <NavLink
                key={v.view}
                label={v.label}
                active={isActive}
                onClick={() => {
                  navigate(`/admin/conversations?view=${v.view}`);
                  onClose?.();
                }}
                mb={2}
                styles={navLinkStyles}
                classNames={{ root: classes.navLink }}
              />
            );
          })}
        </NavLink>

        <NavLink
          label="Per Charger"
          leftSection={<IconGauge size={18} stroke={1.5} />}
          childrenOffset={28}
          mt={2}
          defaultOpened={pathname.startsWith("/admin/chargers")}
          active={pathname.startsWith("/admin/chargers")}
          styles={navLinkStyles}
          classNames={{ root: classes.navLink }}
        >
          {CHARGER_VIEWS.map((v) => {
            const params = new URLSearchParams(window.location.search);
            const currentView = params.get("view") || "list";
            const isActive = pathname.startsWith("/admin/chargers") && currentView === v.view;
            return (
              <NavLink
                key={v.view}
                label={v.label}
                active={isActive}
                onClick={() => {
                  navigate(`/admin/chargers?view=${v.view}`);
                  onClose?.();
                }}
                mb={2}
                styles={navLinkStyles}
                classNames={{ root: classes.navLink }}
              />
            );
          })}
        </NavLink>

        <NavLink
          label="Insights"
          leftSection={<IconChartBar size={18} stroke={1.5} />}
          active={pathname.startsWith("/admin/insights")}
          onClick={() => { navigate("/admin/insights"); onClose?.(); }}
          mb={2}
          mt={2}
          styles={navLinkStyles}
          classNames={{ root: classes.navLink }}
        />

        <NavLink
          label="Team"
          leftSection={<IconUsersGroup size={18} stroke={1.5} />}
          active={pathname.startsWith("/admin/team")}
          onClick={() => { navigate("/admin/team"); onClose?.(); }}
          mb={2}
          mt={2}
          styles={navLinkStyles}
          classNames={{ root: classes.navLink }}
        />

        <NavLink
          label="Tools"
          leftSection={<IconTool size={18} stroke={1.5} />}
          childrenOffset={28}
          mt={4}
          // When the user is on a Tools route, always keep the section
          // expanded so the sibling pages stay reachable (otherwise
          // clicking the parent collapsed it and they'd lose the
          // navigation context for Templates/Email Templates).
          opened={toolsActive || toolsOpen}
          onClick={() => { if (!toolsActive) setToolsOpen((v) => !v); }}
          styles={navLinkStyles}
          classNames={{ root: classes.navLink }}
        >
          {TOOLS_ITEMS.map((item) => (
            <NavLink
              key={item.href}
              label={item.label}
              leftSection={<item.icon size={16} stroke={1.5} />}
              active={pathname.startsWith(item.href)}
              onClick={() => {
                navigate(item.href);
                onClose?.();
              }}
              mb={2}
              styles={navLinkStyles}
              classNames={{ root: classes.navLink }}
            />
          ))}
        </NavLink>
      </AppShell.Section>

      <Divider color="var(--mantine-color-gray-2)" />

      <AppShell.Section p="xs" pb="sm">
        <Menu position="top-start" shadow="md" width={220} withinPortal>
          <Menu.Target>
            <UnstyledButton
              w="100%"
              px="sm"
              py={8}
              style={{
                borderRadius: "var(--mantine-radius-md)",
                transition: "background-color 150ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
            >
              <Group gap={8} wrap="nowrap" justify="space-between">
                <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                  <Avatar size={28} radius="xl" color="indigo" name={user?.name || user?.email || "?"} />
                  <Box style={{ minWidth: 0 }}>
                    <Text size="13px" fw={600} lh={1.2} truncate c="rgba(0,0,0,0.85)">
                      {user?.name || "Loading…"}
                    </Text>
                    <Text size="11px" c="dimmed" lh={1.2} truncate>
                      {user?.email || ""}
                    </Text>
                  </Box>
                </Group>
                <IconChevronUp size={14} stroke={1.75} color="rgba(0,0,0,0.4)" />
              </Group>
            </UnstyledButton>
          </Menu.Target>
          {renderUserMenu()}
        </Menu>
      </AppShell.Section>
    </>
  );

  return (
    <>
      {modal}
      {collapsed && <Box visibleFrom="sm" style={colStyle}>{railContent}</Box>}
      <Box style={colStyle} {...(collapsed ? { hiddenFrom: "sm" as const } : {})}>
        {fullContent}
      </Box>
    </>
  );
}
